import axios from "axios";
import {
  InscriptionFlowRegistration,
  InscriptionFlowRegistrationImpl
} from "../models/responses/InscriptionFlowRegistration";
import { RegistrationSubMode } from "../models/enums/RegistrationSubMode";

const baseUrl =
  "/AutoConnectSSO/api/InscriptionFlow/InscriptionFlowRegistration";

function getFormUrl(flowName: string) {
  return `/${flowName}`;
}

const instance = axios.create({
  baseURL: baseUrl,
  withCredentials: true
});

async function initialization(
  name: string
): Promise<InscriptionFlowRegistration> {
  const url = `${getFormUrl(name)}`;
  const response = await instance.get<InscriptionFlowRegistration>(url);
  const result = new InscriptionFlowRegistrationImpl(
    response.data.guid,
    response.data.registrationMode,
    response.data.registrationSubMode,
    response.data.formType,
    response.data.user,
    ""
  );
  return result;
}

async function registration(
  name: string,
  registrationData: InscriptionFlowRegistration
) {
  let url;
  if (
    registrationData.registrationSubMode == RegistrationSubMode.Update ||
    registrationData.registrationSubMode == RegistrationSubMode.CGUValidation
  ) {
    url = `${getFormUrl(name)}/Update`;
  } else {
    url = `${getFormUrl(name)}/Create`;
  }
  return postRegistration(url, registrationData);
}

async function postRegistration(
  url: string,
  registrationData: InscriptionFlowRegistration
) {
  const formData = getFormData(registrationData);
  const response = await instance.post<InscriptionFlowRegistration>(
    url,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }
  );
  return response.data;
}

async function linkAccount(
  name: string,
  login: string,
  password: string,
  email: string,
  nationalId: string
) {
  const url = `${getFormUrl(name)}/link`;
  const response = await instance.patch<void>(url, {
    login,
    password,
    email,
    nationalId
  });
  return response.data;
}

function getFormData(data: InscriptionFlowRegistration): FormData {
  const formData = new FormData();

  // Base fields
  formData.append("Guid", data.guid);
  formData.append("Field/Title", JSON.stringify({ value: data.user.title }));
  formData.append(
    "Field/FirstName",
    JSON.stringify({ value: data.user.firstName })
  );
  formData.append(
    "Field/LastName",
    JSON.stringify({ value: data.user.lastName })
  );
  formData.append(
    "Field/NationalId",
    JSON.stringify({ value: data.user.nationalId })
  );
  formData.append("Field/Email", JSON.stringify({ value: data.user.email }));
  formData.append("Field/Phone", JSON.stringify({ value: data.user.phone }));
  formData.append(
    "Field/MobilePhone",
    JSON.stringify({ value: data.user.mobilePhone })
  );
  formData.append("Field/Login", JSON.stringify({ value: data.user.login }));
  formData.append(
    "Field/Password",
    JSON.stringify({ value: data.user.password })
  );
  if (data.user.idProfession != null) {
    formData.append(
      "Field/Profession",
      JSON.stringify({ value: data.user.idProfession })
    );
  }
  if (data.user.idSpeciality != null) {
    formData.append(
      "Field/Specialite",
      JSON.stringify({ value: data.user.idSpeciality })
    );
  }
  formData.append(
    "Field/Comment",
    JSON.stringify({ value: data.user.comment })
  );

  //Base checkboxes
  formData.append("Field/CGU", data.user.CGU.toString());
  formData.append("Field/ValidData", data.user.validData.toString());
  formData.append("Field/Newsletter", data.user.newsletter.toString());
  formData.append("CaptchaSolution", data.captchaSolution.toString());

  // Additional fields
  for (const [key, value] of Object.entries(data.user.additionalFields)) {
    if (value.trim() != "") {
      formData.append(`AdditionalField/${key}`, value);
    }
  }

  // Attachment fields
  for (const [key, value] of Object.entries(data.user.attachmentFields)) {
    if (value != null) {
      formData.append(`AttachmentField/${key}`, value);
    }
  }

  // Additional checkboxes
  for (const [key, value] of Object.entries(data.user.additionalCheckboxes)) {
    formData.append(`AdditionalCheckbox/${key}`, value);
  }
  return formData;
}

export const RegistrationService = {
  initialization,
  registration,
  linkAccount
};
